import { Link } from 'react-router-dom';
import { values, teamMembers } from '@/mocks/suviData';
import SEOHead from '@/components/base/SEOHead';

export default function Nosotros() {
  return (
    <>
      <SEOHead
        title="Nosotros | Suvi Digital - Agencia de Marketing para Agencias de Viaje"
        description="Conoce al equipo de Suvi Digital. Agencia de marketing digital especializada en agencias de viaje y tour operadores con presencia en Latinoamérica y España."
        keywords="agencia marketing digital viajes, equipo marketing turismo, Suvi Digital Perú, Hernán Suclla marketing"
        canonical="https://suvidigital.com/nosotros"
        schemaType="Organization"
      />
      <main className="min-h-screen">
        {/* Hero */}
        <section className="relative min-h-[420px] md:min-h-[500px] flex items-center overflow-hidden">
          <div className="absolute inset-0 animate-gradient bg-gradient-to-br from-primary-100 via-background-50 to-secondary-100" />
          <div className="absolute top-20 right-10 w-72 h-72 bg-primary-200/30 rounded-full blur-3xl animate-float-slow" />
          <div className="absolute bottom-20 left-20 w-64 h-64 bg-accent-200/20 rounded-full blur-3xl animate-float-delay-1" />
          <div
            className="absolute inset-0 opacity-[0.03]"
            style={{ backgroundImage: `radial-gradient(circle, oklch(var(--foreground-900)) 1px, transparent 1px)`, backgroundSize: '40px 40px' }}
          />

          <div className="relative z-10 w-full px-4 md:px-6 lg:px-10 xl:px-16 pt-24 md:pt-28">
            <div className="max-w-4xl">
              <span className="inline-block bg-primary-50 text-primary-600 text-sm font-semibold px-4 py-1.5 rounded-full mb-5 border border-primary-200/50">
                Conócenos
              </span>
              <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-foreground-900 mb-5 leading-tight">
                Especialistas en{' '}
                <span className="text-gradient">marketing turístico</span>
              </h1>
              <p className="text-foreground-500 text-lg md:text-xl leading-relaxed max-w-2xl">
                Somos un equipo apasionado por conectar agencias de viaje con viajeros modernos a través de estrategias digitales innovadoras.
              </p>
            </div>
          </div>
        </section>

        {/* Vision */}
        <section className="bg-white py-20 md:py-28">
          <div className="px-4 md:px-6 lg:px-10 xl:px-16">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
              <div>
                <span className="inline-block bg-primary-50 text-primary-600 text-sm font-semibold px-4 py-1.5 rounded-full mb-5 border border-primary-200/50">
                  Nuestra Historia
                </span>
                <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900 mb-6 leading-tight">
                  Nacimos de la{' '}
                  <span className="text-gradient">necesidad real</span>{' '}
                  del sector
                </h2>
                <div className="space-y-5 text-foreground-600 leading-relaxed">
                  <p>
                    <strong className="text-foreground-800">Suvi Digital</strong> nació en 2018 cuando nuestro fundador, Hernán Suclla, identificó una problemática recurrente: las agencias de viajes en Perú y Latinoamérica tenían excelentes productos, pero carecían de una presencia digital profesional que les permitiera competir con gigantes del e-commerce.
                  </p>
                  <p>
                    Desde entonces, hemos trabajado con más de <strong>30 agencias de viaje</strong>, tour operadores y destinos turísticos en <strong>Perú, Chile, Colombia, México y España</strong>.
                  </p>
                  <p>
                    Nuestro enfoque combina <strong>conocimiento profundo del sector turístico</strong> con las últimas tecnologías de marketing digital, IA y automatización.
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-4">
                  <div className="bg-gradient-to-br from-primary-100 to-secondary-100 rounded-2xl p-6 border border-primary-200/40 card-lift">
                    <p className="font-heading text-3xl font-bold text-gradient">30+</p>
                    <p className="text-foreground-500 text-sm mt-1">Agencias asesoradas</p>
                  </div>
                  <div className="bg-gradient-to-br from-accent-100 to-primary-100 rounded-2xl p-6 border border-accent-200/40 card-lift">
                    <p className="font-heading text-3xl font-bold text-gradient">5</p>
                    <p className="text-foreground-500 text-sm mt-1">Países de operación</p>
                  </div>
                </div>
                <div className="space-y-4 pt-8">
                  <div className="bg-gradient-to-br from-secondary-100 to-primary-100 rounded-2xl p-6 border border-secondary-200/40 card-lift">
                    <p className="font-heading text-3xl font-bold text-gradient">7</p>
                    <p className="text-foreground-500 text-sm mt-1">Años de experiencia</p>
                  </div>
                  <div className="bg-gradient-to-br from-primary-100 to-accent-100 rounded-2xl p-6 border border-primary-200/40 card-lift">
                    <p className="font-heading text-3xl font-bold text-gradient">98%</p>
                    <p className="text-foreground-500 text-sm mt-1">Clientes satisfechos</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Values */}
        <section className="bg-background-50 py-20 md:py-28">
          <div className="px-4 md:px-6 lg:px-10 xl:px-16">
            <div className="text-center max-w-3xl mx-auto mb-14 md:mb-20">
              <span className="inline-block bg-primary-50 text-primary-600 text-sm font-semibold px-4 py-1.5 rounded-full mb-4 border border-primary-200/50">
                Nuestros Valores
              </span>
              <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-900 mb-5">
                Lo que nos{' '}
                <span className="text-gradient">define</span>
              </h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {values.map((value, i) => (
                <div
                  key={value.title}
                  className="card-lift bg-white border border-background-200/60 rounded-2xl p-6 text-center group"
                  style={{ animationDelay: `${i * 100}ms` }}
                >
                  <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-gradient-to-br from-primary-100 to-secondary-100 text-primary-600 mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                    <i className={`${value.icon} text-2xl`} />
                  </div>
                  <h3 className="font-heading font-bold text-lg text-foreground-900 mb-2">{value.title}</h3>
                  <p className="text-foreground-500 text-sm leading-relaxed">{value.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Team */}
        <section className="bg-white py-20 md:py-28">
          <div className="px-4 md:px-6 lg:px-10 xl:px-16">
            <div className="text-center max-w-3xl mx-auto mb-14 md:mb-20">
              <span className="inline-block bg-primary-50 text-primary-600 text-sm font-semibold px-4 py-1.5 rounded-full mb-4 border border-primary-200/50">
                Nuestro Equipo
              </span>
              <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-900 mb-5">
                Personas apasionadas{' '}
                <span className="text-gradient">por los viajes</span>
              </h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 max-w-5xl mx-auto">
              {teamMembers.map((member) => (
                <div
                  key={member.name}
                  className="card-lift bg-background-50 border border-background-200/60 rounded-2xl overflow-hidden group"
                >
                  <div className="relative h-52 md:h-60 overflow-hidden">
                    <img
                      src={member.image}
                      alt={member.name}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-primary-900/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  </div>
                  <div className="p-6">
                    <h3 className="font-heading font-bold text-lg text-foreground-900">{member.name}</h3>
                    <p className="text-primary-600 text-sm font-medium mb-3">{member.role}</p>
                    <p className="text-foreground-500 text-sm leading-relaxed">{member.bio}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="relative py-20 md:py-28 overflow-hidden">
          <div className="absolute inset-0 animate-gradient bg-gradient-to-r from-primary-700 via-primary-600 to-secondary-600" />
          <div className="absolute top-10 left-[15%] w-56 h-56 bg-white/5 rounded-full blur-3xl animate-float" />
          <div className="absolute bottom-10 right-[15%] w-72 h-72 bg-accent-500/10 rounded-full blur-3xl animate-float-delay-1" />

          <div className="relative z-10 px-4 md:px-6 lg:px-10 xl:px-16">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-5">
                ¿Listo para trabajar{' '}
                <span className="text-accent-300">juntos?</span>
              </h2>
              <p className="text-white/70 text-base md:text-lg leading-relaxed mb-10 max-w-2xl mx-auto">
                Conocemos el sector turístico y sabemos cómo hacer crecer tu agencia de viajes en el entorno digital.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/contacto"
                  className="btn-glow bg-accent-500 hover:bg-accent-600 text-white font-semibold px-8 py-4 rounded-xl text-center transition-colors whitespace-nowrap text-lg"
                >
                  Agenda una Reunión
                </Link>
                <Link
                  to="/servicios"
                  className="bg-white/15 hover:bg-white/25 text-white font-semibold px-8 py-4 rounded-xl text-center transition-colors border border-white/30 whitespace-nowrap text-lg"
                >
                  Ver Servicios
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>
    </>
  );
}